/**
 * Session Timeout Manager (BRO-458)
 * Simple client-side approach: read session lifetime from meta tag, set timer, show warning
 * No server polling - only calls server when user clicks "Stay Logged In"
 */
console.debug('[SessionTimeout] Script loaded');

class SessionTimeoutManager {
    constructor() {
        this.warningSeconds = 5 * 60; // Show warning 5 min before expiry
        this.secondsRemaining = null;
        this.warningShown = false;
        this.warningTimer = null;
        this.expiryTimer = null;
        this.sessionLifetimeSeconds = null; // Store for reset after extend

        this.init();
    }

    init() {
        // Get actual seconds remaining from server
        const secondsMeta = document.querySelector('meta[name="session-seconds-remaining"]');
        if (!secondsMeta) {
            console.debug('[SessionTimeout] No session-seconds-remaining meta tag found, disabled');
            return;
        }

        this.secondsRemaining = parseInt(secondsMeta.content, 10);
        if (isNaN(this.secondsRemaining) || this.secondsRemaining <= 0) {
            console.debug('[SessionTimeout] Invalid seconds remaining:', secondsMeta.content);
            return;
        }

        console.debug('[SessionTimeout] Seconds remaining:', this.secondsRemaining);

        // Schedule warning and expiry timers
        this.scheduleTimers();

        // Sync across tabs
        window.addEventListener('storage', (e) => {
            if (e.key === 'session_extended') {
                console.debug('[SessionTimeout] Session extended in another tab');
                this.resetTimers();
                this.hideWarning();
            }
        });
    }

    scheduleTimers() {
        // Clear existing timers
        if (this.warningTimer) clearTimeout(this.warningTimer);
        if (this.expiryTimer) clearTimeout(this.expiryTimer);

        const expiryMs = this.secondsRemaining * 1000;
        const warningMs = this.warningSeconds * 1000;
        const warningDelay = expiryMs - warningMs;

        // Schedule warning (5 min before expiry)
        if (warningDelay > 0) {
            console.debug('[SessionTimeout] Warning in:', warningDelay / 1000, 'seconds');
            this.warningTimer = setTimeout(() => {
                if (!this.warningShown) this.showWarning();
            }, warningDelay);
        } else {
            // Session expires in less than warning time - show warning immediately
            console.debug('[SessionTimeout] Session expires soon, showing warning immediately');
            this.showWarning();
        }

        // Schedule expiry redirect
        console.debug('[SessionTimeout] Expiry in:', expiryMs / 1000, 'seconds');
        this.expiryTimer = setTimeout(() => {
            this.handleExpired();
        }, expiryMs);
    }

    resetTimers(newSecondsRemaining = null) {
        console.debug('[SessionTimeout] resetTimers called with:', newSecondsRemaining);
        // After extending session, use full session lifetime
        if (newSecondsRemaining) {
            this.secondsRemaining = newSecondsRemaining;
        }
        console.debug('[SessionTimeout] New secondsRemaining:', this.secondsRemaining);
        this.warningShown = false;
        this.scheduleTimers();
    }

    showWarning() {
        console.debug('[SessionTimeout] Showing warning');
        this.warningShown = true;
        window.dispatchEvent(new CustomEvent('session-timeout-warning'));
    }

    hideWarning() {
        this.warningShown = false;
        window.dispatchEvent(new CustomEvent('session-timeout-hide'));
    }

    async extendSession() {
        console.debug('[SessionTimeout] Extending session...');
        try {
            // This single API call refreshes the session
            const response = await window.axios.post('/api/session/refresh');

            // Hide warning first, then reset timers (order matters:
            // resetTimers may re-show warning immediately if session < 5 min)
            const newSeconds = response.data.seconds_remaining;
            this.hideWarning();
            this.resetTimers(newSeconds);

            // Notify other tabs
            localStorage.setItem('session_extended', Date.now().toString());

            console.debug('[SessionTimeout] Session extended successfully, new timeout:', newSeconds, 'seconds');
        } catch (error) {
            console.debug('[SessionTimeout] Failed to extend session:', error);
            if (error.response?.status === 401) {
                this.handleExpired();
            }
        }
    }

    handleExpired() {
        console.debug('[SessionTimeout] Session expired, redirecting to login');
        sessionStorage.setItem('intended_url', window.location.pathname + window.location.search);

        // BRO-692: Redirect via GET instead of POSTing to /logout with a stale CSRF token.
        // The session is already expired server-side, so there's nothing to actively log out.
        window.location.href = '/login?session_expired=1';
    }
}

// Auto-init on authenticated pages
console.debug('[SessionTimeout] Registering DOMContentLoaded listener');
document.addEventListener('DOMContentLoaded', () => {
    console.debug('[SessionTimeout] DOMContentLoaded fired');
    const authEl = document.querySelector('[data-authenticated]');
    console.debug('[SessionTimeout] data-authenticated element:', authEl);
    if (authEl) {
        console.debug('[SessionTimeout] Creating SessionTimeoutManager');
        window.sessionTimeoutManager = new SessionTimeoutManager();
    }
});
